import gsap from "gsap";
import { SplitText } from "gsap/src/SplitText";
import { ScrollTrigger } from "gsap/src/ScrollTrigger";
import Lenis from "lenis";


gsap.registerPlugin(SplitText, ScrollTrigger);

// ✅ Conditional Lenis initialization: Disable on Safari
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const lenis = isSafari ? null : new Lenis({
  duration: 1.5,
  easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
  smoothWheel: true,
  smoothTouch: false
});

// ✅ Export Lenis instance for global access
export { lenis };

// Animation frame loop to sync Lenis with GSAP
function raf(time) {
  if (lenis) lenis.raf(time);
  requestAnimationFrame(raf);
}
requestAnimationFrame(raf);

// ✅ Fix: Ensure Lenis updates when the page loads and lazy images load
let resizeTimeout;

function refreshLenis() {
  clearTimeout(resizeTimeout); // Cancel previous calls
  resizeTimeout = setTimeout(() => {
    requestAnimationFrame(() => {
      if (lenis) lenis.resize(); // ✅ Recalculate dimensions smoothly
    });
  }, 3000); // Adjust delay if necessary
}

// ✅ Refresh Lenis when the page fully loads (not on Safari)
if (!isSafari) {
  window.addEventListener("load", refreshLenis);
}



function createParallaxEffect() {
  const containers = gsap.utils.toArray('.parallax-img');

  containers.forEach(container => {
    const img = container.querySelector('img');

    if (!img) {
      console.warn("No <img> found in", container);
      return;
    }

    const setupParallax = () => {
      requestAnimationFrame(() => {
        const containerHeight = container.offsetHeight;
        const parallaxOffset = containerHeight * 0.15;
        const requiredHeight = containerHeight + parallaxOffset;
        img.style.height = `${requiredHeight}px`;

        gsap.fromTo(img, {
          y: -parallaxOffset
        }, {
          y: 0,
          ease: "none",
          scrollTrigger: {
            trigger: container,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.5,
            invalidateOnRefresh: true
          }
        });
      });
    };

    // Load handling
    if (img.complete && img.naturalHeight !== 0) {
      setupParallax();
    } else {
      img.addEventListener('load', setupParallax);
    }

    // Optional: re-run on resize for responsiveness, debounced
    let resizeDebounce;
    window.addEventListener("resize", () => {
      clearTimeout(resizeDebounce);
      resizeDebounce = setTimeout(() => {
        ScrollTrigger.getAll().forEach(st => st.kill());
        setupParallax();
        ScrollTrigger.refresh(true);
      }, 250);
    });
  });
}



function pinSections() {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  gsap.utils.toArray('.pinned').forEach(section => {
    const isPhoneDisabled = section.classList.contains('disable-phone');
    if (isTouchDevice && isPhoneDisabled) return;

    gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top top",
        end: "bottom top",
        pin: true,
        pinSpacing: false,
        scrub: true
      }
    });
  });
}

function ParallaxContent() {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  if (isTouchDevice) return;

  gsap.utils.toArray('.parallax-content').forEach(el => {
    const speed = el.dataset.speed ? parseFloat(el.dataset.speed) : -20; // default: up

    gsap.to(el, {
      yPercent: speed,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  });
}


// ✅ NEW FUNCTION
// ✅ SEO/AT-safe text reveal (no container hiding, ARIA-friendly, PRM-aware)
function animateTextReveal() {
  const targets = document.querySelectorAll(".TextReveal");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  
  targets.forEach(target => {
    const delay = parseFloat(target.getAttribute("data-delay")) || 0;
    let hasAnimated = false;
    
    // Capture original text for accessibility and fallback
    const originalText = (target.textContent || "").trim();
    
    const cleanup = () => {
      if (target._split) {
        target._split.revert();
        target._split = null;
      }
      if (target._stID) {
        ScrollTrigger.getById(target._stID)?.kill();
        target._stID = null;
      }
    };
    
    const run = () => {
      cleanup();
      
      // If user prefers reduced motion, don't split/animate—just show the text
      if (reduceMotion) {
        target.style.visibility = "visible";
        target.style.opacity = "1";
        return;
      }
      
      // Check if element is a heading or other semantic element that shouldn't have role="text"
      const tagName = target.tagName.toLowerCase();
      const isHeading = /^h[1-6]$/.test(tagName);
      const isSemanticElement = ['p', 'span', 'div', 'section', 'article'].includes(tagName);
      
      // Only apply role="text" and aria-label to appropriate elements
      if (!isHeading && isSemanticElement) {
        target.setAttribute("role", "text");
        if (originalText) target.setAttribute("aria-label", originalText);
      }
      
      // Important: keep the container visible; animate children only
      gsap.set(target, { visibility: "visible", opacity: 1 });
      
      const split = new SplitText(target, { type: "words,chars" });
      target._split = split;
      
      // Hide chars for animation, but from AT perspective they are hidden anyway
      split.chars.forEach(ch => ch.setAttribute("aria-hidden", "true"));
      gsap.set(split.chars, { opacity: 0, y: 10 });
      
      // If already in view and previously animated, show immediately
      const rect = target.getBoundingClientRect();
      const isInView = rect.top < window.innerHeight - 100;
      
      if (hasAnimated && isInView) {
        gsap.set(split.chars, { opacity: 1, y: 0 });
        return;
      }
      
      // Unique id for cleanup between refreshes
      const uniqueId = `textReveal-${Date.now()}-${Math.random()}`;
      const st = ScrollTrigger.create({
        id: uniqueId,
        trigger: target,
        start: "top bottom-=100",
        once: true,
        onEnter: () => {
          if (!target._split) return;
          gsap.to(target._split.chars, {
            duration: 0.065,
            opacity: 1,
            y: 0,
            ease: "circ.out",
            stagger: 0.01,
            delay,
            onComplete: () => { hasAnimated = true; }
          });
        }
      });
      
      target._stID = uniqueId;
    };
    
    run();
    
    // Rebuild on meaningful resizes
    let initialW = window.innerWidth, initialH = window.innerHeight, tId;
    const handleResize = () => {
      clearTimeout(tId);
      tId = setTimeout(() => {
        const wDelta = Math.abs(window.innerWidth - initialW);
        const hDelta = Math.abs(window.innerHeight - initialH);
        if (wDelta > 100 || hDelta > 100) {
          initialW = window.innerWidth;
          initialH = window.innerHeight;
          run();
        }
      }, 250);
    };
    
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", () => setTimeout(handleResize, 500));
  });
}


function setupOffCanvasMenu() {
  const menuToggle = document.querySelector('.burger');
  const menu = document.querySelector('.side-drawer');
  const overlay = document.querySelector('.overlay');
  const menuItems = document.querySelectorAll('.navbar-side-item');
  const [line1, line2, line3] = ['line1', 'line2', 'line3'].map(id => document.getElementById(id));

  if (!menuToggle || !menu || !overlay || !line1 || !line2 || !line3) {
    console.warn('Off-canvas menu elements missing.');
    return;
  }

  const menuTimeline = gsap.timeline({
    paused: true,
    reversed: true,
    onStart: () => {
      document.body.classList.add('menu-open');
    },
    onReverseComplete: () => {
      document.body.classList.remove('menu-open');
    }
  });

  menuTimeline
    .set(menu, { x: '100%' }) // ensure starts offscreen
    .to(menu, { x: '0%', duration: 0.4, ease: 'power3.out' }, 0)
    .to(overlay, { opacity: 0.7, pointerEvents: 'auto', duration: 0.4 }, 0)
    .from(menuItems, {
      y: 20,
      opacity: 0,
      duration: 0.35,
      stagger: 0.06,
      ease: 'power2.out'
    }, '-=0.2');

  const burgerTimeline = gsap.timeline({ paused: true, reversed: true })
    .to(line1, { rotate: 45, y: 6, transformOrigin: 'center', duration: 0.25 }, 0)
    .to(line2, { opacity: 0, duration: 0.25 }, 0)
    .to(line3, { rotate: -45, y: -6, transformOrigin: 'center', duration: 0.25 }, 0);

  const openMenu = () => {
    menuTimeline.play();
    burgerTimeline.play();
    menuToggle.setAttribute('aria-expanded', 'true');
  };

  const closeMenu = () => {
    menuTimeline.reverse();
    burgerTimeline.reverse();
    menuToggle.setAttribute('aria-expanded', 'false');
  };

  const toggleMenu = () => {
    menuTimeline.reversed() ? openMenu() : closeMenu();
  };

  menuToggle.addEventListener('click', toggleMenu);
  overlay.addEventListener('click', closeMenu);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMenu();
  });
}



// Subtle slide-right effect on hover for navbar items
function subtleNavHoverEffect() {
  const navItems = document.querySelectorAll('#navbarSide > li');

  navItems.forEach(item => {
    item.addEventListener('mouseenter', () => {
      gsap.to(item, { x: 6, duration: 0.25, ease: 'power2.out' });
    });

    item.addEventListener('mouseleave', () => {
      gsap.to(item, { x: 0, duration: 0.25, ease: 'power2.out' });
    });
  });
}


function initImageTrailEffect() {
  const wrapper = document.querySelector('.image-trail');
  if (!wrapper) return;
  const images = Array.from(wrapper.querySelectorAll('img'));
  if (images.length < 2) return;

  const MathUtils = {
    lerp: (a, b, n) => (1 - n) * a + n * b,
    distance: (x1, y1, x2, y2) => Math.hypot(x2 - x1, y2 - y1)
  };

  let mousePos = { x: 0, y: 0 };
  let lastMousePos = { x: 0, y: 0 };
  let cacheMousePos = { x: 0, y: 0 };
  let isInsideWrapper = false;

  // Check if point is within wrapper bounds
  const isPointInWrapper = (clientX, clientY) => {
    const rect = wrapper.getBoundingClientRect();
    return clientX >= rect.left && 
           clientX <= rect.right && 
           clientY >= rect.top && 
           clientY <= rect.bottom;
  };

  // Handle both mouse and touch events
  const updatePosition = (clientX, clientY) => {
    isInsideWrapper = isPointInWrapper(clientX, clientY);
    if (isInsideWrapper) {
      mousePos = { x: clientX, y: clientY };
    }
  };

  // Mouse events
  wrapper.addEventListener('mousemove', e => {
    updatePosition(e.clientX, e.clientY);
  });

  wrapper.addEventListener('mouseenter', e => {
    updatePosition(e.clientX, e.clientY);
    lastMousePos = { x: e.clientX, y: e.clientY };
    cacheMousePos = { x: e.clientX, y: e.clientY };
  });

  wrapper.addEventListener('mouseleave', () => {
    isInsideWrapper = false;
  });

  // Touch events
  wrapper.addEventListener('touchmove', e => {
    e.preventDefault(); // Prevent scrolling during touch
    const touch = e.touches[0];
    updatePosition(touch.clientX, touch.clientY);
  }, { passive: false });

  // Handle touch start to initialize position
  wrapper.addEventListener('touchstart', e => {
    const touch = e.touches[0];
    updatePosition(touch.clientX, touch.clientY);
    if (isInsideWrapper) {
      lastMousePos = { x: touch.clientX, y: touch.clientY };
      cacheMousePos = { x: touch.clientX, y: touch.clientY };
    }
  });

  wrapper.addEventListener('touchend', () => {
    isInsideWrapper = false;
  });

  class TrailImage {
    constructor(el) {
      this.el = el;
      this.updateRect();
    }

    updateRect() {
      this.rect = this.el.getBoundingClientRect();
    }

    isActive() {
      return gsap.isTweening(this.el) || this.el.style.opacity !== '0';
    }
  }

  class ImageTrail {
    constructor() {
      this.images = images.map(img => new TrailImage(img));
      this.imgPosition = 0;
      this.zIndexVal = 1;
      // Reduce threshold for touch devices for better responsiveness
      this.threshold = ('ontouchstart' in window) ? 60 : 80;
      requestAnimationFrame(() => this.render());
    }

    render() {
      // Only process if inside wrapper
      if (!isInsideWrapper) {
        requestAnimationFrame(() => this.render());
        return;
      }

      const dist = MathUtils.distance(mousePos.x, mousePos.y, lastMousePos.x, lastMousePos.y);
      
      // Slightly faster lerp for touch devices
      const lerpFactor = ('ontouchstart' in window) ? 0.35 : 0.25;
      cacheMousePos.x = MathUtils.lerp(cacheMousePos.x, mousePos.x, lerpFactor);
      cacheMousePos.y = MathUtils.lerp(cacheMousePos.y, mousePos.y, lerpFactor);

      if (dist > this.threshold) {
        this.showNextImage();
        lastMousePos = { ...mousePos };
        this.imgPosition = (this.imgPosition + 1) % this.images.length;
        this.zIndexVal++;
      }

      if (this.images.every(img => !img.isActive())) {
        this.zIndexVal = 1;
      }

      requestAnimationFrame(() => this.render());
    }

    showNextImage() {
      const img = this.images[this.imgPosition];
      gsap.killTweensOf(img.el);
      img.updateRect();

      gsap.set(img.el, {
        opacity: 1,
        scale: 1,
        zIndex: this.zIndexVal,
        x: cacheMousePos.x - img.rect.width / 2,
        y: cacheMousePos.y - img.rect.height / 2
      });

      gsap.fromTo(img.el, { opacity: 0 }, { 
        opacity: 1, 
        duration: 0.1, 
        ease: 'power1.out' 
      });

      const tl = gsap.timeline();
      tl.to(img.el, {
        x: mousePos.x - img.rect.width / 2,
        y: mousePos.y - img.rect.height / 2,
        ease: 'expo.out',
        duration: 1
      }, 0)
        .to(img.el, {
          opacity: 0,
          scale: 0.2,
          ease: 'power1.out',
          duration: 0.6
        }, '+=0.8');
    }
  }

  const preloadImages = () => {
    return Promise.all(images.map(img => {
      return new Promise(resolve => {
        if (img.complete) resolve();
        else img.addEventListener('load', resolve);
      });
    }));
  };

  preloadImages().then(() => new ImageTrail());
}


function animateSwitchRevealImages() {
  const items = document.querySelectorAll('.swish-reveal');

  items.forEach(item => {
    const cover = item.querySelector('.swish-cover');

    if (!cover) return;

    gsap.fromTo(cover, {
      x: '0%'
    }, {
      x: '100%',
      duration: 0.7,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: item,
        start: 'top 80%',
        toggleActions: 'play none none none'
      }
    });
  });
}





function createStackedGallery({
  selector = ".stackedgallery",     // container with <img> children
  scrub = true,
  fade = false                      // set true if you want a slight fade as each slides up
} = {}) {
  const gallery = document.querySelector(selector);
  if (!gallery) return;

  const images = Array.from(gallery.querySelectorAll("img"));
  if (images.length < 2) return;

  // Base styles
  gsap.set(gallery, { position: "relative", height: "100vh", overflow: "hidden" });
  gsap.set(images, {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    willChange: "transform, opacity",
    zIndex: i => images.length - i
  });

  // Build one pinned timeline that spans all slides
  const totalDistance = (images.length - 1) * window.innerHeight;

  // (Optional) keep ST in sync with Lenis
  if (typeof lenis !== "undefined" && lenis) {
    lenis.on("scroll", () => ScrollTrigger.update());
  }

  const tl = gsap.timeline({
    defaults: { ease: "none" },
    scrollTrigger: {
      trigger: gallery,
      start: "top top",
      end: `+=${totalDistance}`,
      scrub,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true
    }
  });

  // Each image (except the bottom one) slides up by 100% over its segment
  const segment = 1 / (images.length - 1);
  images.forEach((img, i) => {
    if (i === images.length - 1) return; // last image stays as base layer
    // Slide current image up
    tl.to(img, { yPercent: -100 }, i * segment);

    // Optional aesthetic fade while it slides out
    if (fade) {
      tl.to(img, { opacity: 0.0 }, i * segment);
    }
  });

  // Refresh on resize to keep distances correct
  let rAF;
  const onResize = () => {
    cancelAnimationFrame(rAF);
    rAF = requestAnimationFrame(() => {
      const newDistance = (images.length - 1) * window.innerHeight;
      tl.scrollTrigger?.vars && (tl.scrollTrigger.vars.end = `+=${newDistance}`);
      tl.scrollTrigger?.refresh();
      if (typeof lenis !== "undefined" && lenis) lenis.resize?.();
    });
  };
  window.addEventListener("resize", onResize);
}





export default {
  init: function() {
    pinSections();
    ParallaxContent();
    createParallaxEffect();
    animateTextReveal();
    setupOffCanvasMenu();
    subtleNavHoverEffect();
    initImageTrailEffect();
    animateSwitchRevealImages();
    createStackedGallery();
  }
};